import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { type MockInspection, type MockInspectionResult } from '@/mocks/qms';
import { qcService } from '@/services/qcService';
import InspectionFormModal from '@/pages/manufacturing/qc/incoming/components/InspectionFormModal';
import { useWarehouseStore } from '@/stores/warehouseStore';
import ViewInspectionModal from '@/pages/manufacturing/qc/incoming/components/ViewInspectionModal';
import NCRPromptModal from '@/pages/manufacturing/qc/incoming/components/NCRPromptModal';
import NewFinalSlideOver from './components/NewFinalSlideOver';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';

const statusBadge = (status: MockInspection['status']) => {
  const map: Record<string, string> = {
    PASSED: 'bg-emerald-100 text-emerald-700',
    FAILED: 'bg-red-100 text-red-700',
    PENDING: 'bg-amber-100 text-amber-700',
    IN_PROGRESS: 'bg-sky-100 text-sky-700',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
};

export default function FinalQCPage() {
  const navigate = useNavigate();
  const selectedWarehouseId = useWarehouseStore((state) => state.selectedWarehouseId);

  const [inspections, setInspections] = useState<MockInspection[]>([]);
  const [results, setResults] = useState<MockInspectionResult[]>([]);
  const [loading, setLoading] = useState(true);

  const loadInspections = useCallback(async () => {
    try {
      setLoading(true);
      const data = await qcService.getInspections({
        type: 'FINAL',
        warehouseId: selectedWarehouseId || undefined,
      });
      setInspections(data || []);
    } catch (err) {
      console.error('Error loading final inspections:', err);
      setInspections([]);
    } finally {
      setLoading(false);
    }
  }, [selectedWarehouseId]);

  useEffect(() => {
    loadInspections();
  }, [loadInspections]);

  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [poFilter, setPoFilter] = useState('');

  const [showNewInspection, setShowNewInspection] = useState(false);
  const [activeInspection, setActiveInspection] = useState<MockInspection | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showNCRPrompt, setShowNCRPrompt] = useState(false);
  const [ncrInspection, setNcrInspection] = useState<MockInspection | null>(null);

  const finalInspections = useMemo(
    () => inspections.filter((i) => i.type === 'FINAL'),
    [inspections]
  );

  const filtered = useMemo(() => {
    return finalInspections.filter((i) => {
      if (statusFilter && i.status !== statusFilter) return false;
      if (dateFrom && i.scheduledDate < dateFrom) return false;
      if (dateTo && i.scheduledDate > dateTo) return false;
      if (poFilter && !i.sourceNumber.toLowerCase().includes(poFilter.toLowerCase())) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          i.inspectionNumber.toLowerCase().includes(q) ||
          i.itemName.toLowerCase().includes(q) ||
          i.sourceNumber.toLowerCase().includes(q) ||
          i.checklistName.toLowerCase().includes(q) ||
          (i.inspectorName && i.inspectorName.toLowerCase().includes(q))
        );
      }
      return true;
    });
  }, [finalInspections, statusFilter, search, dateFrom, dateTo, poFilter]);

  const stats = useMemo(() => {
    const total = finalInspections.length;
    const passed = finalInspections.filter((i) => i.status === 'PASSED').length;
    const failed = finalInspections.filter((i) => i.status === 'FAILED').length;
    const pending = finalInspections.filter((i) => i.status === 'PENDING').length;
    return { total, passed, failed, pending };
  }, [finalInspections]);

  const handleNewInspection = useCallback(async (newInsp: MockInspection) => {
    try {
      await qcService.createInspection(newInsp);
      await loadInspections();
    } catch (err) {
      console.error('Error creating final inspection:', err);
    } finally {
      setShowNewInspection(false);
    }
  }, [loadInspections]);

  const handleStartInspection = useCallback((insp: MockInspection) => {
    setActiveInspection(insp);
  }, []);

  const handleViewInspection = useCallback((insp: MockInspection) => {
    setActiveInspection(null);
    setShowViewModal(true);
    setActiveInspection(insp);
  }, []);

  const handleSubmitInspection = useCallback(
    async (newResults: MockInspectionResult[], overallStatus: 'PASSED' | 'FAILED') => {
      if (!activeInspection) return;

      try {
        await qcService.submitInspectionResults(activeInspection.id, {
          results: newResults,
          overallStatus
        });
        await loadInspections();
      } catch (err) {
        console.error('Error submitting inspection results:', err);
      }

      const targetInsp = activeInspection;
      setActiveInspection(null);

      if (overallStatus === 'FAILED') {
        const updated = { ...targetInsp, status: overallStatus as MockInspection['status'] };
        setNcrInspection(updated);
        setShowNCRPrompt(true);
      }
    },
    [activeInspection, loadInspections]
  );

  const handleCreateNCR = useCallback((inspection: MockInspection) => {
    navigate('/manufacturing/qc/ncr', { state: { fromInspection: inspection.id } });
  }, [navigate]);

  const handleNCRSkip = useCallback(() => {
    // Inspection already marked FAILED
  }, []);

  const existingResultsForActive = useMemo(() => {
    if (!activeInspection) return [];
    return results.filter((r) => r.inspectionId === activeInspection.id);
  }, [activeInspection, results]);

  const resultsForView = useMemo(() => {
    if (!activeInspection || !showViewModal) return [];
    return results.filter((r) => r.inspectionId === activeInspection.id);
  }, [activeInspection, showViewModal, results]);

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Final Quality Inspection</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Final inspection before goods dispatch
            </p>
          </div>
          <button
            onClick={() => setShowNewInspection(true)}
            className="px-3 py-2 bg-violet-600 text-white text-sm font-medium rounded-md hover:bg-violet-700 whitespace-nowrap"
          >
            <i className="ri-add-line mr-1" />
            New Inspection
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <i className="ri-file-list-3-line text-gray-600 text-lg" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
                <p className="text-xs text-gray-500">Total Inspections</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <i className="ri-check-double-line text-emerald-600 text-lg" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-emerald-700">{stats.passed}</p>
                <p className="text-xs text-gray-500">Passed</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                <i className="ri-close-circle-line text-red-600 text-lg" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-red-700">{stats.failed}</p>
                <p className="text-xs text-gray-500">Failed</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <i className="ri-time-line text-amber-600 text-lg" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-amber-700">{stats.pending}</p>
                <p className="text-xs text-gray-500">Pending</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-col lg:flex-row gap-3 items-end">
            <div className="flex-1 w-full lg:w-auto">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Search</label>
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search inspection, item, PO..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
            </div>
            <div className="w-full lg:w-44">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Status</label>
              <SearchableSelect
                value={statusFilter}
                onChange={(val) => setStatusFilter(val)}
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'PASSED', label: 'Passed' },
                  { value: 'FAILED', label: 'Failed' },
                  { value: 'PENDING', label: 'Pending' },
                  { value: 'IN_PROGRESS', label: 'In Progress' }
                ]}
                placeholder="All Status"
              />
            </div>
            <div className="w-full lg:w-40">
              <label className="text-xs font-medium text-gray-500 mb-1 block">Production Order</label>
              <input
                type="text"
                value={poFilter}
                onChange={(e) => setPoFilter(e.target.value)}
                placeholder="Filter by PO..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div className="w-full lg:w-40">
              <label className="text-xs font-medium text-gray-500 mb-1 block">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div className="w-full lg:w-40">
              <label className="text-xs font-medium text-gray-500 mb-1 block">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <button
              onClick={() => {
                setStatusFilter('');
                setSearch('');
                setDateFrom('');
                setDateTo('');
                setPoFilter('');
              }}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-600 hover:bg-gray-50 whitespace-nowrap"
            >
              <i className="ri-filter-off-line mr-1" />
              Clear
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Insp No</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Production Order</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Item</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700">Qty</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700">Sample</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">Result</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Inspector</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Date</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">Status</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-12 text-center text-gray-400">
                      <div className="flex flex-col items-center gap-2">
                        <i className="ri-inbox-line text-3xl" />
                        <p>No final inspections found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((insp) => (
                    <tr key={insp.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{insp.inspectionNumber}</td>
                      <td className="px-4 py-3 text-gray-600">
                        <div className="flex flex-col">
                          <span className="font-medium text-sm">{insp.sourceNumber}</span>
                          <span className="text-xs text-gray-400">{insp.sourceType}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="flex flex-col">
                          <span className="font-medium text-sm">{insp.itemName}</span>
                          {insp.variantName && (
                            <span className="text-xs text-gray-400">{insp.variantName}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 text-sm">
                        {insp.totalQty} {insp.unit}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 text-sm">
                        {insp.sampleQty} {insp.unit}
                        <div className="text-xs text-gray-400">{insp.samplingPlan.replace('_', ' ')}</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {insp.status === 'PASSED' && (
                          <span className="text-emerald-600 font-medium text-sm">{insp.passedQty} OK</span>
                        )}
                        {insp.status === 'FAILED' && (
                          <span className="text-red-600 font-medium text-sm">{insp.failedQty} Fail</span>
                        )}
                        {(insp.status === 'PENDING' || insp.status === 'IN_PROGRESS') && (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700 text-sm">
                        {insp.inspectorName || <span className="text-gray-400 text-xs">Unassigned</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-sm">
                        <div className="flex flex-col">
                          <span>{insp.scheduledDate}</span>
                          {insp.completedDate && (
                            <span className="text-xs text-gray-400">Done: {insp.completedDate}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(
                            insp.status
                          )}`}
                        >
                          {insp.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {insp.status === 'PENDING' && (
                            <button
                              onClick={() => handleStartInspection(insp)}
                              className="px-2 py-1 text-xs bg-violet-50 text-violet-700 hover:bg-violet-100 rounded font-medium"
                              title="Start Inspection"
                            >
                              <i className="ri-play-circle-line mr-0.5" />
                              Start
                            </button>
                          )}
                          {insp.status !== 'PENDING' && (
                            <button
                              onClick={() => handleViewInspection(insp)}
                              className="p-1.5 text-gray-500 hover:text-violet-600 hover:bg-violet-50 rounded"
                              title="View"
                            >
                              <i className="ri-eye-line" />
                            </button>
                          )}
                          {insp.status === 'FAILED' && (
                            <button
                              onClick={() => navigate('/manufacturing/qc/ncr')}
                              className="px-2 py-1 text-xs bg-red-50 text-red-700 hover:bg-red-100 rounded font-medium"
                              title="NCR"
                            >
                              NCR
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modals */}
      <NewFinalSlideOver
        isOpen={showNewInspection}
        onClose={() => setShowNewInspection(false)}
        onSave={handleNewInspection}
      />

      {activeInspection && !showViewModal && (
        <InspectionFormModal
          inspection={activeInspection}
          existingResults={existingResultsForActive}
          onClose={() => setActiveInspection(null)}
          onSubmit={handleSubmitInspection}
        />
      )}

      {activeInspection && showViewModal && (
        <ViewInspectionModal
          inspection={activeInspection}
          results={resultsForView}
          onClose={() => {
            setShowViewModal(false);
            setActiveInspection(null);
          }}
        />
      )}

      <NCRPromptModal
        inspection={ncrInspection}
        onClose={() => {
          setShowNCRPrompt(false);
          setNcrInspection(null);
        }}
        onCreateNCR={handleCreateNCR}
        onSkip={handleNCRSkip}
      />
    </AppLayout>
  );
}